import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { FeatureToggleService, hd } from '@nx-fe/services';
import { BroadcastService, BroadcastChannels } from 'app/utils/broadcastService';
import { BehaviorSubject, timer, Subscription ,  Observable } from 'rxjs';
import { DeviceCheckinCompleted, DeviceCheckinResultCode } from '../models/device-checkin-completed';
import { DeviceCheckinFailure } from '../models/device-checkin-failure';
import { CheckinStateBroadcastService, DeviceIdToCheckinStateMap, DeviceCheckinStatusMessage, DeviceStatusRequest } from './checkin.broadcaster';

@Injectable({
    providedIn: 'root'
})
export class DeviceCheckinStateService implements OnInit, OnDestroy {
    public readonly checkinBroadcaster: CheckinStateBroadcastService;
    private readonly deviceSnapshotRequestInProgress: BroadcastService<hd.DeviceSnapshotRequestStatus> = new BroadcastService(BroadcastChannels.onDeviceSnapshotRequestInProgress);
    private readonly nonSnapshotTabSelected: BroadcastService<void> = new BroadcastService(BroadcastChannels.onSnapshotViewVisibilityChanged);
    private readonly deviceCheckinKey = 'deviceCheckinLockout';

    private deviceCheckinCompletedSubscription: Subscription;
    private deviceCheckinErrorSubscription: Subscription;

    constructor(
        private featureToggleService: FeatureToggleService
    ) {
        this.deviceCheckinCompleted$ = this.deviceCheckinCompletedSubject.asObservable();
        this.deviceCheckinError$ = this.deviceCheckinErrorSubject.asObservable();
        this.deviceCheckinInProgress$ = this.deviceCheckinInProgressSubject.asObservable();
        this.checkinBroadcaster = new CheckinStateBroadcastService();

        this.checkinBroadcaster.onMessage = this.processMessage;

        this.deviceCheckinCompletedSubscription = this.deviceCheckinCompleted$.subscribe(() => {
            this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{ requestName: this.deviceCheckinKey, inProgress: false});
        });

        this.deviceCheckinErrorSubscription = this.deviceCheckinError$.subscribe(() => {
            this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{ requestName: this.deviceCheckinKey, inProgress: false});
        });

        this.nonSnapshotTabSelected.onMessage = (() => {
            this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{ requestName: this.deviceCheckinKey, inProgress: false });
        });

        // ngOnInit isn't called via downgrade service, will call manually
        this.init();
    }

    deviceCheckinCompleted$: Observable<DeviceCheckinCompleted>;
    deviceCheckinError$: Observable<DeviceCheckinFailure>;
    deviceCheckinInProgress$: Observable<DeviceIdToCheckinStateMap>;

    private _checkinStatus: DeviceIdToCheckinStateMap = {};

    get checkinStatus(): DeviceIdToCheckinStateMap {
        return this._checkinStatus;
    }

    set checkinStatus(newValue: DeviceIdToCheckinStateMap) {
        this._checkinStatus = newValue || {};
        this.deviceCheckinInProgressSubject.next(this._checkinStatus);
    }

    private deviceCheckinInProgressSubject = new BehaviorSubject<DeviceIdToCheckinStateMap>(this._checkinStatus);
    private deviceCheckinCompletedSubject = new BehaviorSubject<DeviceCheckinCompleted>(null);
    private deviceCheckinErrorSubject = new BehaviorSubject<DeviceCheckinFailure>(null);

    private initCheckinStatus(deviceId: string) {
        this._checkinStatus[deviceId] = new Date();
        this.checkinStatus = this._checkinStatus;
    }

    public startCheckinInProgress = (deviceId: string) => {
        this.initCheckinStatus(deviceId);
        this.checkinBroadcaster.postMessage({
            request: DeviceStatusRequest.WatchDevice,
            deviceId
        });
        this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{ requestName: this.deviceCheckinKey, inProgress: true});
    };

    public stopCheckinInProgress = (deviceId: string) => {
        this.checkinBroadcaster.postMessage({
            request: DeviceStatusRequest.StopWatchingDevice,
            deviceId
        });
        this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{ requestName: this.deviceCheckinKey, inProgress: false});
    };

    private processMessage = (msg: DeviceCheckinStatusMessage) => {
        this.checkinStatus = msg.deviceCheckinStatus;
        switch (msg.request) {
            case DeviceStatusRequest.Status:
                break;
            case DeviceStatusRequest.Error:
                this.deviceCheckinErrorSubject.next(msg.data as DeviceCheckinFailure);
                break;
            case DeviceStatusRequest.Completed:
            case DeviceStatusRequest.Timeout:
                this.deviceCheckinCompletedSubject.next(msg.data as DeviceCheckinCompleted);
                break;
        }
    };

    private broadcastLockout = (isLockedOut: hd.DeviceSnapshotRequestStatus) => {
        this.deviceSnapshotRequestInProgress.postMessage(isLockedOut);
    };

    ngOnInit(): void {

    }

    private init(): void {
        this.checkinBroadcaster.postMessage({
            request: DeviceStatusRequest.Refresh
        });
    }

    ngOnDestroy(): void {
        if (this.checkinBroadcaster) {
            this.checkinBroadcaster.close();
        }
        if (this.deviceSnapshotRequestInProgress) {
            this.deviceSnapshotRequestInProgress.close();
        }
        if (this.nonSnapshotTabSelected) {
            this.nonSnapshotTabSelected.close();
        }

        if (this.deviceCheckinCompletedSubscription) {
            this.deviceCheckinCompletedSubscription.unsubscribe();
        }

        if (this.deviceCheckinErrorSubscription) {
            this.deviceCheckinErrorSubscription.unsubscribe();
        }
    }
}
